import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { fetchCaseSettings, updateCaseSettings } from "../../../../api/caseSettings";

const emptySettings = {
  notify_automatic_case_email: true,
  automatic_case_notify_email: "",
  company_email: "",
  effective_recipient: "",
};

const normalize = (data = {}) => ({
  notify_automatic_case_email: data.notify_automatic_case_email !== false,
  automatic_case_notify_email: data.automatic_case_notify_email || "",
  company_email: data.company_email || "",
  effective_recipient: data.effective_recipient || "",
});

const snapshotOf = (data) =>
  JSON.stringify({
    notify_automatic_case_email: data.notify_automatic_case_email !== false,
    automatic_case_notify_email: (data.automatic_case_notify_email || "").trim(),
  });

export default function AutomaticCaseEmailSettings() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(emptySettings);
  const [savedSnapshot, setSavedSnapshot] = useState(snapshotOf(emptySettings));

  const applyLoaded = useCallback((data) => {
    const next = normalize(data);
    setSettings(next);
    setSavedSnapshot(snapshotOf(next));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchCaseSettings()
      .then((data) => {
        if (!cancelled) applyLoaded(data);
      })
      .catch(() => {
        if (!cancelled) toast.error(t("cases.settings.notify.load_failed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applyLoaded, t]);

  const dirty = useMemo(
    () => snapshotOf(settings) !== savedSnapshot,
    [settings, savedSnapshot]
  );

  const patchSettings = (patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await updateCaseSettings({
        notify_automatic_case_email: settings.notify_automatic_case_email === true,
        automatic_case_notify_email: settings.automatic_case_notify_email.trim(),
      });
      applyLoaded(data);
      toast.success(t("cases.settings.notify.saved"));
    } catch {
      toast.error(t("cases.settings.notify.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <p className="text-sm text-slate-500">{t("cases.settings.notify.loading")}</p>
      </section>
    );
  }

  const recipientMissing = !settings.effective_recipient && settings.notify_automatic_case_email;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-slate-950">
          {t("cases.settings.notify.title")}
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          {t("cases.settings.notify.description")}
        </p>
      </div>
      <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
        <input
          type="checkbox"
          checked={settings.notify_automatic_case_email === true}
          onChange={(event) =>
            patchSettings({ notify_automatic_case_email: event.target.checked })
          }
          className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600"
        />
        <div>
          <div className="text-sm font-medium text-slate-900">
            {t("cases.settings.notify.toggle")}
          </div>
          <div className="text-sm text-slate-600">
            {t("cases.settings.notify.toggle_hint")}
          </div>
        </div>
      </label>
      <div className="mt-4">
        <label className="block text-sm font-medium text-slate-900">
          {t("cases.settings.notify.override_label")}
        </label>
        <p className="mt-1 text-sm text-slate-600">
          {t("cases.settings.notify.override_hint", {
            email: settings.company_email || t("cases.settings.notify.no_company_email"),
          })}
        </p>
        <input
          type="email"
          value={settings.automatic_case_notify_email}
          onChange={(event) =>
            patchSettings({ automatic_case_notify_email: event.target.value })
          }
          placeholder={settings.company_email || ""}
          className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600"
        />
      </div>
      {recipientMissing && (
        <p className="mt-3 text-sm text-amber-700">
          {t("cases.settings.notify.missing_email")}
        </p>
      )}
      <div className="mt-4">
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={handleSave}
          className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? t("cases.settings.notify.saving") : t("cases.settings.notify.save")}
        </button>
      </div>
    </section>
  );
}
